import AssembleCastIcon from "@/assets/icons/AssembleCast.svg";
import MaleIcon from "@/assets/icons/MaleIcon.svg";
import FemaleIcon from "@/assets/icons/FemaleIcon.svg";
import InfoIcon from "@/assets/icons/InfoIcon.svg";
import { Plus } from "lucide-react";
import { CustomButton } from "@/components/ui/Buttons";
import { Dispatch, SetStateAction, useState } from "react";
import { TAdditionalActorDetail } from "@/redux/feature/jobs/jobsType";
import VideoUploaderWithPreview from "@/components/ui/VideoUploaderWithPreview";
import { FileDetails } from "@/types/types";

const AssembleCastModal = ({
  setOpen,
  onClick,
  isLoading,
  actorData,
  uploadedVideos,
  setUploadedVideos,
}: {
  isLoading: boolean;
  actorData: TAdditionalActorDetail[];
  onClick: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  uploadedVideos: {
    role?: string;
    video?: FileDetails;
    hash?: string;
    thumbnail?: string;
  }[];
  setUploadedVideos: Dispatch<
    SetStateAction<
      {
        role?: string;
        video?: FileDetails;
        hash?: string;
        thumbnail?: string;
      }[]
    >
  >;
}) => {
  return (
    <div className="space-y-6">
      {/* Heading */}
      <div className="flex items-center gap-x-4 ">
        <AssembleCastIcon />
        <div>
          <h2 className="text-[16px] font-semibold ">Assemble the Cast</h2>
          <p className="text-[12px]  text-[#475467]">
            We need more actors. Please suggest colleagues you can bring; client{" "}
            <br />
            approval required for the shoot.
          </p>
        </div>
      </div>

      {/* Info */}
      <div className="flex px-4 py-2 italic items-center gap-2 rounded-full bg-[#FAFAFA] ">
        <InfoIcon />
        <p className="text-xs text-[#252525] ">
          Please include a short video (under 60 seconds) that demonstrates
          their{" "}
          <span className="font-semibold">
            on-camera presence and delivery style.
          </span>
        </p>
      </div>

      {/* Roles Card */}

      {actorData?.map((role, index) => (
        <div
          key={index}
          className="border flex gap-x-4 justify-between rounded-[8px] p-4 border-[#EEEEEE]"
        >
          <div className="flex max-w-[355px] justify-between flex-col">
            <div className="flex items-center gap-x-4">
              <MaleIcon />
              <h3 className="text-[14px] text-[#000000] font-semibold">
                Role {index + 1} -{" "}
                <span className="text-[#9E9E9E]">"{role.actorRole}"</span>
              </h3>
            </div>

            <p className="text-[#475467] text-xs pb-[0px]">
              Looking for a {role.gender} actor. Age: ({role.age.ageRange.min}–
              {role.age.ageRange.max}
              {role.bodyType?.title ? `, ${role.bodyType.title}` : ""}) for a{" "}
              {role.personaStyles} role.
              {role.wardrobeStyle?.length > 0 && (
                <>
                  {" "}
                  Wardrobe: {role.wardrobeStyle.map((w) => w.title).join(", ")}.
                </>
              )}
              {role.additionalNotes && <> {role.additionalNotes}</>}
            </p>
            <div></div>
          </div>

          <div className="w-[138px] relative">
            <VideoUploaderWithPreview
              height="138px"
              width="138px"
              cardDesignSize=" h-[138px] w-[138px]"
              accept={{
                "video/mp4": [".mp4"],
                "video/quicktime": [".mov"],
                "video/webm": [],
                "video/ogg": [],
              }}
              maxFiles={1}
              useLiveUrl={false}
              onFilesUploaded={(value) => {
                const newVideo = {
                  role: role.actorRole,
                  video: value[0] as FileDetails,
                  hash: value[0]?.hash as string,
                  thumbnail: value[0]?.thumbnail as string,
                };

                setUploadedVideos((prev) => {
                  const filtered = prev.filter(
                    (v) => v?.role !== role?.actorRole
                  );
                  return newVideo.video? [...filtered, newVideo]:[...filtered];
                });
              }}
            />
            {/* <Plus /> */}
          </div>
        </div>
      ))}

      <p className="text-[#475467] text-sm">
        As the <span className="font-bold">Team Lead</span>, you'll be
        responsible for coordinating with Co-actors and ensuring timely
        submissions.
      </p>
      <div className="flex flex-col md:flex-row w-full gap-4 mt-6">
        <CustomButton
          onClick={() => setOpen(false)}
          variant="outline"
          color="#344054"
          fontSize="14px"
          fontWeight="500"
          padding="10px 24px"
          border="1px solid #E8EBEE"
          borderRadius="8px"
          className="leading-5 w-full"
        >
          Cancel
        </CustomButton>
        <CustomButton
          fontSize="14px"
          // CustomButton forwards the click event to its handler. Invoke the
          // submit callback explicitly so that event is not sent as part of
          // the application payload.
          onClick={() => void onClick()}
          fontWeight="500"
          disabled={isLoading}
          padding="10px 24px"
          backgroundColor="#17457D"
          borderRadius="8px"
          className="leading-5 w-full"
        >
          {isLoading ? "Loading" : "Submit"}
        </CustomButton>
      </div>
    </div>
  );
};

export default AssembleCastModal;
